/**
 * wiki.service.ts — Wiki domain.
 *
 * Wraps the wiki API. The backend wraps payloads in `{ success, data }`;
 * these helpers unwrap and return `data` directly, matching the pattern
 * used by documents.service.ts.
 */

import {
  getRequest,
  postRequest,
  postRawResponse,
  putRequest,
  patchRequest,
  deleteRequest,
  postFormData,
} from "./http.service";
import {
  URL_WIKI_PAGES,
  URL_WIKI_SEARCH,
  URL_WIKI_PAGE_BY_ID,
  URL_WIKI_PAGE_LOCK,
  URL_WIKI_UPLOAD_IMAGE,
  URL_WIKI_UPLOAD_VIDEO,
  URL_WIKI_VIDEO_SIGNED_URL,
  URL_WIKI_REORDER,
  URL_WIKI_PAGE_VISIBILITY,
  URL_WIKI_PAGE_PERMISSIONS,
  URL_WIKI_PAGE_PERMISSION,
  URL_CONTACTS_LIST,
  URL_CLIENTS,
  URL_WIKI_PAGE_DRAFT,
  URL_WIKI_PAGE_SUMMARIZE,
  URL_WIKI_FOLDERS,
  URL_WIKI_PAGE_MOVE,
  URL_WIKI_PAGE_NOTIFY_ALL,
  URL_WIKI_NOTIFICATIONS,
  URL_WIKI_NOTIFICATIONS_UNREAD_COUNT,
  URL_WIKI_NOTIFICATIONS_READ_ALL,
  URL_WIKI_NOTIFICATION_READ,
  URL_WIKI_NOTIFICATION_DELETE,
  URL_WIKI_PAGE_COMMENTS,
  URL_WIKI_COMMENT_BY_ID,
  URL_WIKI_COMMENT_RESOLVE,
  URL_WIKI_PAGE_VERSIONS,
  URL_WIKI_PAGE_VERSION,
} from "./url.service";
import { WikiPage, WikiPageSummary, WikiPermissionEntry } from "../types/wiki.types";

export interface MentionItem {
  id: string;
  label: string;
  // Loosened beyond the wiki's original "contact" | "client" so FloatingChat's
  // rule-command (#) and template (~) pickers can reuse this list/dropdown
  // shape too — the chip just renders whatever short tag is passed here.
  type: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

const unwrap = <T>(p: Promise<unknown>): Promise<T> =>
  p.then((res) => (res as Envelope<T>).data);

export interface WikiSearchResult {
  _id:       string;
  title:     string;
  snippet:   string;
  updatedAt: string;
}

export const fetchWikiPages = () =>
  unwrap<WikiPageSummary[]>(getRequest(URL_WIKI_PAGES));

export const searchWikiContent = (q: string) =>
  unwrap<WikiSearchResult[]>(getRequest(`${URL_WIKI_SEARCH}?q=${encodeURIComponent(q)}`));

export const fetchWikiPage = (id: string) =>
  unwrap<WikiPage>(getRequest(URL_WIKI_PAGE_BY_ID(id)));

export const createWikiPage = (title: string, parentId: string | null = null) =>
  unwrap<WikiPage>(postRequest(URL_WIKI_PAGES, { title, parentId }));

export const saveWikiDraft = (id: string, payload: Partial<Pick<WikiPage, "title" | "content">>) =>
  unwrap<WikiPage>(postRequest(URL_WIKI_PAGE_DRAFT(id), payload));

export const updateWikiPage = (
  id: string,
  payload: Partial<Pick<WikiPage, "title" | "content">>,
) => unwrap<WikiPage>(putRequest(URL_WIKI_PAGE_BY_ID(id), payload));

export const deleteWikiPage = (id: string) =>
  deleteRequest(URL_WIKI_PAGE_BY_ID(id));

export const lockWikiPage = (id: string) =>
  unwrap<WikiPage>(postRequest(URL_WIKI_PAGE_LOCK(id), {}));

export const unlockWikiPage = (id: string) =>
  deleteRequest(URL_WIKI_PAGE_LOCK(id));

/** Export a wiki page as a PDF — pre-rendered Mermaid SVGs and current HTML content are sent in the body */
export const exportWikiPagePdf = (id: string, renderedDiagrams: (string | null)[], content: string) =>
  postRawResponse(`${URL_WIKI_PAGE_BY_ID(id)}/export`, { format: "pdf", renderedDiagrams, content });

export const uploadWikiImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("image", file);
  const res = await postFormData<{ success: boolean; url: string }>(
    URL_WIKI_UPLOAD_IMAGE,
    formData,
  );
  return res.url;
};

export interface WikiVideoUploadResult {
  url: string;
  externalId?: string;
  sharepointDriveId?: string;
  storagePath?: string;
}

export const uploadWikiVideo = async (file: File): Promise<WikiVideoUploadResult> => {
  const formData = new FormData();
  formData.append("video", file);
  const res = await postFormData<WikiVideoUploadResult>(URL_WIKI_UPLOAD_VIDEO, formData);
  return res;
};

export const fetchWikiVideoSignedUrl = (externalId: string, driveId: string): Promise<string> =>
  getRequest<{ url: string }>(
    `${URL_WIKI_VIDEO_SIGNED_URL}?externalId=${encodeURIComponent(externalId)}&driveId=${encodeURIComponent(driveId)}`,
  ).then((r) => r.url);

// Fetches all org contacts + clients and filters client-side by query.
// Both datasets are small (org-scoped), so client-side filtering is fine.
export const searchMentions = async (query: string): Promise<MentionItem[]> => {
  if (!query.trim()) return [];
  const q = query.toLowerCase();

  const [contactsRes, clientsRes] = await Promise.all([
    getRequest(URL_CONTACTS_LIST),
    getRequest(URL_CLIENTS),
  ]);

  const contacts = ((contactsRes as any)?.contacts ?? []) as any[];
  const clients = ((clientsRes as any) ?? []) as any[];

  const matchedContacts: MentionItem[] = contacts
    .filter((c) => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
    .slice(0, 5)
    .map((c) => ({ id: c._id, label: c.name || c.email, type: "contact" as const }));

  const matchedClients: MentionItem[] = clients
    .filter((c) => c.name?.toLowerCase().includes(q))
    .slice(0, 5)
    .map((c) => ({ id: c._id, label: c.name, type: "client" as const }));

  return [...matchedContacts, ...matchedClients].slice(0, 8);
};

export const setPageVisibility = (id: string, visibility: WikiPage["visibility"]) =>
  unwrap<WikiPage>(putRequest(URL_WIKI_PAGE_VISIBILITY(id), { visibility }));

export const fetchPagePermissions = (id: string) =>
  unwrap<WikiPermissionEntry[]>(getRequest(URL_WIKI_PAGE_PERMISSIONS(id)));

export const addPagePermission = (id: string, userId: string, role: "viewer" | "editor") =>
  postRequest(URL_WIKI_PAGE_PERMISSIONS(id), { userId, role });

export const removePagePermission = (id: string, userId: string) =>
  deleteRequest(URL_WIKI_PAGE_PERMISSION(id, userId));

export const summarizeWikiPage = (id: string): Promise<{ summary: string }> =>
  postRequest(URL_WIKI_PAGE_SUMMARIZE(id), {}).then((res: any) => res.data);

export const createWikiFolder = (title: string, parentId: string | null = null): Promise<WikiPageSummary> =>
  postRequest(URL_WIKI_FOLDERS, { title, parentId }).then((res: any) => res.data);

export const moveWikiItem = (id: string, parentId: string | null, sortOrder: number): Promise<WikiPageSummary> =>
  patchRequest(URL_WIKI_PAGE_MOVE(id), { parentId, sortOrder }).then((res: any) => res.data);

export const reorderWikiItems = (
  items: { id: string; sortOrder: number; parentId?: string | null }[],
): Promise<void> => patchRequest(URL_WIKI_REORDER, { items }).then(() => undefined);

export const notifyAllMentioned = (id: string): Promise<{ notified: number }> =>
  postRequest(URL_WIKI_PAGE_NOTIFY_ALL(id), {}).then((res: any) => res.data);

// ── Wiki notifications ───────────────────────────────────────────────────────

export interface WikiNotification {
  id:           string;
  type:         "pageMention" | "commentMention" | "notifyAll" | "pageEdit" | "commented";
  message:      string;
  isRead:       boolean;
  createdAt:    string;
  wikiPageId:   string;
  wikiPageTitle: string;
  actor:        { id: string; name: string };
}

export interface WikiNotificationPage {
  notifications: WikiNotification[];
  total:         number;
  page:          number;
  hasMore:       boolean;
}

export const fetchWikiNotifications = (page = 1) =>
  unwrap<WikiNotificationPage>(
    getRequest(`${URL_WIKI_NOTIFICATIONS}?page=${page}`)
  );

export const fetchWikiUnreadCount = () =>
  unwrap<{ count: number }>(getRequest(URL_WIKI_NOTIFICATIONS_UNREAD_COUNT));

export const markWikiNotificationRead = (id: string) =>
  patchRequest(URL_WIKI_NOTIFICATION_READ(id), {});

export const markAllWikiNotificationsRead = () =>
  postRequest(URL_WIKI_NOTIFICATIONS_READ_ALL, {});

export const deleteWikiNotification = (id: string) =>
  deleteRequest(URL_WIKI_NOTIFICATION_DELETE(id));

export const clearAllWikiNotifications = () =>
  deleteRequest(URL_WIKI_NOTIFICATIONS);

export interface WikiComment {
  id:              string;
  wikiPageId:      string;
  parentCommentId: string | null;
  content:         string;
  selectedText:    string;
  resolvedAt:      string | null;
  resolvedById:    string | null;
  createdAt:       string;
  updatedAt:       string;
  creator: { id: string; name: string };
}

export const fetchComments = (pageId: string) =>
  unwrap<WikiComment[]>(getRequest(URL_WIKI_PAGE_COMMENTS(pageId)));

export const createComment = (
  pageId: string,
  payload: { content: string; selectedText?: string; parentCommentId?: string | null },
) => unwrap<WikiComment>(postRequest(URL_WIKI_PAGE_COMMENTS(pageId), payload));

export const updateComment = (pageId: string, commentId: string, content: string) =>
  unwrap<WikiComment>(putRequest(URL_WIKI_COMMENT_BY_ID(pageId, commentId), { content }));

export const deleteComment = (pageId: string, commentId: string) =>
  deleteRequest(URL_WIKI_COMMENT_BY_ID(pageId, commentId));

export const resolveComment = (pageId: string, commentId: string, resolved: boolean) =>
  unwrap<WikiComment>(patchRequest(URL_WIKI_COMMENT_RESOLVE(pageId, commentId), { resolved }));

// ── Wiki page versions ───────────────────────────────────────────────────────

export interface WikiVersionSummary {
  _id:           string;
  versionNumber: number;
  title:         string;
  savedByName:   string;
  createdAt:     string;
}

export interface WikiVersionFull extends WikiVersionSummary {
  content: string;
}

export const fetchVersions = (pageId: string) =>
  unwrap<WikiVersionSummary[]>(getRequest(URL_WIKI_PAGE_VERSIONS(pageId)));

export const fetchVersion = (pageId: string, versionId: string) =>
  unwrap<WikiVersionFull>(getRequest(URL_WIKI_PAGE_VERSION(pageId, versionId)));
